import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardBody } from '../ui/Card';
import StatusPill from '../ui/StatusPill';
import { DetailPageHeader } from '../ui/DetailPageHeader';
import { coStatusLabel, coStatusTone, coTitle, isCoDraftLike } from './coDisplay';
import { Alert } from '../ui/Alert';
import { SkeletonCard } from '../ui/Skeleton';
// DropdownMenu import removed — PT-49 CTA rule replaced it with named buttons
import api from '../../services/api';
import { AttachModal } from '../attachments/AttachModal';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import { Table, TableHead, TableBody, TableRow, TableHeader } from '../ui/Table';
import { LineRow } from '../signature/ChangeOrderSignView';
import ReopenConfirmDialog, { canReopenCo, REOPEN_SUCCESS, REOPEN_FAILURE } from './ReopenConfirmDialog';
import { useToast } from '../ui/Toast';
import { formatCurrency } from '../../utils/formatCurrency';
import { getJobContractTotal } from '../../utils/jobContractTotal';
import { acceptedLine, isESigned } from '../shared/acceptedLine';
import AcknowledgmentsPanel from '../acknowledgments/AcknowledgmentsPanel';
import { linePrice } from '../../change-order/coPrice';

// IDI-454: rows are named and counted as the signing page reads them
// (coprojection.go): productLabel, item_name, description; sqft, sq_ft,
// quantity. An added builder line has no description or quantity.
function lineName(item) {
  return item.productLabel || item.item_name || item.description || '—';
}

function lineCount(item) {
  for (const k of ['sqft', 'sq_ft', 'quantity']) {
    const v = item[k];
    if (v === null || v === undefined || v === '') continue;
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

// Same format as Review & Send (ChangeOrderReviewSend.js fmtCents).
function fmtCents(cents) {
  return `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// IDI-454 (D1, D3, D4): only added rows show a price, read by coPrice;
// an unreadable price is "Needs price", never $0.00.
function addedPriceText(item) {
  const p = linePrice(item);
  return p === null ? 'Needs price' : fmtCents(p);
}

// ─── CO status display ───────────────────────────────────────────────────────
// IDI-556: labels and tones come from ./coDisplay (Draft, Sent, Approved,
// Rejected). Stored statuses are unchanged.

// ─── Send ────────────────────────────────────────────────────────────────────

// IDI-464 (Adrian 10-20): Send and Resend open Review & Send, which emails the
// signer the link and shows what happened. The old send modal (and its
// PDF-only option, which marked the CO sent without mailing anything) is
// deleted, and so is the in-place resend that only stamped a timestamp.

// ─── Main CO Detail Component ────────────────────────────────────────────────

export default function ChangeOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [decisionOpen, setDecisionOpen] = useState(false);
  // IDI-460 (D1): Add to Job's final check, read on click, never on mount.
  const [applyPreview, setApplyPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [co, setCO] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false); // A2: CO-scope attachments
  const [reopenOpen, setReopenOpen] = useState(false); // IDI-639
  const [reopening, setReopening] = useState(false);
  const toast = useToast();
  // PT-35.2a: showKebab state retired — DropdownMenu atom owns its own open state.
  // const [showKebab, setShowKebab] = useState(false);

  useEffect(() => {
    fetchChangeOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchChangeOrder = async () => {
    try {
      setLoading(true);
      const data = await api.getChangeOrder(id);
      setCO(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setCO(null);
      } else {
        setError('Failed to load change order');
      }
    } finally {
      setLoading(false);
    }
  };

  const openReviewSend = () => navigate(`/change-orders/${id}/review`);

  // IDI-556: Withdraw is removed (Adrian 10-21 ruling 5). A sent CO is
  // changed with Reopen and Edit (IDI-639); an old withdrawn CO acts as a
  // draft.
  const handleReopen = async () => {
    setReopening(true);
    try {
      await api.reopenChangeOrder(id);
      toast?.success(REOPEN_SUCCESS);
      navigate(`/jobs/${co?.jobId || co?.job_id}/change-orders/${id}/build`);
    } catch (err) {
      setReopenOpen(false);
      setError(REOPEN_FAILURE);
    } finally {
      setReopening(false);
    }
  };

  // IDI-330: this used to be ~120 lines of client-computed merge +
  // divergence-check logic, written as TWO separate, non-atomic PUTs (job,
  // then CO). That's now a single transactional server endpoint —
  // POST /v1/change-orders/{id}/apply — which does the SAME merge and
  // divergence check (ported line-for-line, see cmd/changeorders/apply.go),
  // but atomically and without a client-bypassable safety check. See
  // ChangeOrderDetail's earlier revision history for the old client logic
  // if it's ever needed for reference.
  // IDI-460: "Add to Job" (ruled 09-10, confirmed 10-06) — and it confirms
  // first, because it mutates the JOB: the change order's lines are merged
  // into it and its subtotal/tax/total are recomputed server-side.
  // D-06 (Adrian 10-08): the confirm is the final check. It lists the lines,
  // the net change, tax and the job's new total, read from the customer view
  // (the same projection the signer saw) and the job, both on click. If
  // either can't be read, no dialog opens.
  const handleApproveAndApply = async () => {
    const jobId = co?.jobId || co?.job_id;
    try {
      setPreviewLoading(true);
      if (!jobId) throw new Error('change order has no job');
      const [view, job] = await Promise.all([
        api.getChangeOrderCustomerView(id),
        api.getJob(jobId),
      ]);
      if (!view || !job) throw new Error('empty read');
      setApplyPreview({ view, job });
    } catch (err) {
      setError("Couldn't load this change order's lines. Try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmApply = async () => {
    setApplyPreview(null);
    try {
      setActionLoading(true);
      const updated = await api.applyChangeOrder(id);
      setCO(updated?.data || updated || { ...co, status: 'applied' });
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage || 'Failed to approve and apply change order');
    } finally {
      setActionLoading(false);
    }
  };

  // IDI-331: manual attestation path (phone/in-person/hard-copy acceptance).
  // Permissions-gated server-side (admin/admin_sales only, CONTROL 2) — the
  // client-side role check below is a UX convenience (hide the button for
  // users who can't use it), NOT the security boundary; the 403 from the
  // backend is.
  // IDI-460: the channel was hard-coded to 'phone' behind a bare confirm(),
  // so a recorded acceptance carried no evidence of HOW it happened.
  // IDI-547: change orders follow the quote rule — no channel picker; a
  // hand-recorded acceptance is stored as "manual". The dialog asks only for
  // a note; who and when are captured server-side. The
  // quote's own page is not reused: it exists to convert a quote to a job,
  // which has no meaning for an amendment to an existing job.
  const handleRecordDecision = () => setDecisionOpen(true);

  const submitRecordDecision = async ({ notes }) => {
    try {
      setActionLoading(true);
      setDecisionOpen(false);
      const updated = await api.recordChangeOrderDecision(id, {
        notes,
        recorder: user?.email || user?.name || '',
      });
      setCO(updated?.data || updated || { ...co, status: 'accepted' });
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      setError(serverMessage || 'Failed to record decision');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="space-y-4 p-6"><SkeletonCard /><SkeletonCard /></div>;

  if (!co) {
    return (
      <div className="px-6">
        <Alert variant="error">Change order not found</Alert>
        <Button variant="ghost" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4 mr-2" /> Back
        </Button>
      </div>
    );
  }

  const status = co.status || 'draft';
  const lineItems = co.lines || co.lineItems || co.line_items || [];
  const history = co.history || [];

  return (
    <div className="px-6 max-w-4xl mx-auto">
      {/* Header */}
      <DetailPageHeader
        backHref={(co.jobId || co.job_id) ? `/jobs/${co.jobId || co.job_id}?tab=change-orders` : '/jobs'}
        backLabel="Back"
        title={coTitle(co)}
        status={status}
        statusLabel={coStatusLabel(status)}
        statusTone={coStatusTone(status)}
        /* IDI-823 (D5): only what the record holds; no lone "•", no extra read. */
        subtitle={[co.jobTitle || co.job_title, co.customerName || co.customer_name]
          .filter(Boolean).join(' \u2022 ') || undefined}
      />

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status-dependent CTAs */}
      <div className="flex items-center gap-3 mb-6">
        {/* §2 (2026-08-08): Attach button wires the already-mounted AttachModal. */}
        <Button variant="secondary" size="sm" onClick={() => setAttachOpen(true)}>
          Attach
        </Button>
        <div className="ml-auto" />
        {isCoDraftLike(status) && (
          <Button
            onClick={openReviewSend}
            variant="primary"
          >
            <PaperAirplaneIcon className="h-4 w-4 mr-1.5" />
            Send
          </Button>
        )}
        {status === 'sent' && (
          /* PT-49 CTA rule: named buttons, not a dropdown — Record Decision is
             conditionally rendered (admin/admin_sales only), so this is
             visually 2 or 3 buttons depending on role, never a 4th. */
          <>
            <Button onClick={openReviewSend} variant="secondary">
              <ArrowPathIcon className="h-4 w-4 mr-1.5" />
              Resend
            </Button>
            {/* IDI-331: manual attestation, admin/admin_sales only (server-enforced
                403 is the real boundary; this hides it client-side for everyone
                else since they'd just get an error). */}
            {(user?.role === 'admin' || user?.groups?.includes('admin_sales') || user?.groups?.includes('admin')) && (
              <Button onClick={handleRecordDecision} variant="secondary" loading={actionLoading}>
                <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                Record Decision
              </Button>
            )}
          </>
        )}
        {status === 'declined' && (
          /* IDI-452: customer declined (reads "Rejected"). Revise and send again. */
          <Button onClick={openReviewSend} variant="primary">
            <PaperAirplaneIcon className="h-4 w-4 mr-1.5" />
            Send again
          </Button>
        )}
        {canReopenCo(user, status) && (
          <Button onClick={() => setReopenOpen(true)} variant="secondary">
            Reopen and Edit
          </Button>
        )}
        {status === 'accepted' && (
          <Button
            onClick={handleApproveAndApply}
            loading={actionLoading || previewLoading}
            variant="primary"
          >
            <CheckCircleIcon className="h-4 w-4 mr-1.5" />
            Add to Job
          </Button>
        )}
      </div>
      <ReopenConfirmDialog
        open={reopenOpen}
        status={status}
        loading={reopening}
        onClose={() => setReopenOpen(false)}
        onConfirm={handleReopen}
      />

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column — Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <span className="text-sm font-medium text-gray-700">Description</span>
            </CardHeader>
            <CardBody>
              <p className="text-sm text-gray-900">{co.description || 'No description'}</p>
              {co.reason && (
                <p className="text-sm text-gray-500 mt-2">
                  <span className="font-medium">Reason:</span> {co.reason}
                </p>
              )}
            </CardBody>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <span className="text-sm font-medium text-gray-700">Line Items</span>
              <span className="text-xs text-gray-500">{lineItems.length} item{lineItems.length !== 1 ? 's' : ''}</span>
            </CardHeader>
            <CardBody className="p-0">
              {lineItems.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {lineItems.map((item, idx) => (
                    <div key={item.id || idx} className={[
                      'flex items-center justify-between px-4 py-3',
                      // PT-44 (2026-07-28): per-line status treatment on detail view.
                      item.status === 'cancelled' ? 'bg-gray-100 border-l-4 border-l-gray-300' :
                      item.status === 'added' ? 'bg-green-50 border-l-4 border-l-green-400' :
                      item.status === 'changed' ? 'bg-amber-50 border-l-4 border-l-amber-400' :
                      item.locked ? 'bg-gray-50 border-l-4 border-l-gray-300' :
                      '',
                    ].filter(Boolean).join(' ')}>
                      <div>
                        <p className={[
                          'text-sm',
                          item.status === 'cancelled' ? 'line-through text-gray-400' :
                          item.locked ? 'text-gray-500' :
                          'text-gray-900',
                        ].filter(Boolean).join(' ')}>
                          {lineName(item)}
                          {item.status === 'added' && <span className="ml-2 text-xs font-semibold text-green-700">New</span>}
                          {item.status === 'cancelled' && <span className="ml-2 text-xs font-semibold text-gray-400">Cancelled</span>}
                          {item.status === 'changed' && <span className="ml-2 text-xs font-semibold text-amber-700">Changed</span>}
                        </p>
                        <p className="text-xs text-gray-500">{item.unit || 'ea'}</p>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        × {lineCount(item)}
                        {item.status === 'added' && ` · ${addedPriceText(item)}`}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 px-4 py-6 text-center">No line items</p>
              )}
            </CardBody>
          </Card>

          {/* IDI-542: the change order's acknowledgment history. */}
          <Card>
            <CardHeader>
              <span className="text-sm font-medium text-gray-700">Acknowledgments</span>
            </CardHeader>
            <CardBody className="p-0">
              <AcknowledgmentsPanel documentType="change_order" documentId={id} />
            </CardBody>
          </Card>
        </div>

        {/* Right Column — Status & History */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <span className="text-sm font-medium text-gray-700">Status</span>
            </CardHeader>
            <CardBody>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Current Status</span>
                  <StatusPill status={status} label={coStatusLabel(status)} tone={coStatusTone(status)} />
                </div>
                {co.createdAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Created</span>
                    <span className="text-xs text-gray-700">{new Date(co.createdAt).toLocaleDateString()}</span>
                  </div>
                )}
                {co.sentAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Sent</span>
                    <span className="text-xs text-gray-700">{new Date(co.sentAt).toLocaleDateString()}</span>
                  </div>
                )}
                {co.approvedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Approved</span>
                    <span className="text-xs text-gray-700">{new Date(co.approvedAt).toLocaleDateString()}</span>
                  </div>
                )}
                {co.appliedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Added to job</span>
                    <span className="text-xs text-gray-700">{new Date(co.appliedAt).toLocaleDateString()}</span>
                  </div>
                )}
                <RecordedDecision co={co} />
              </div>
            </CardBody>
          </Card>

          {/* History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <span className="text-sm font-medium text-gray-700">History</span>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {history.map((event, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <ClockIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-900">{event.action || event.description}</p>
                        <p className="text-xs text-gray-500">
                          {event.timestamp ? new Date(event.timestamp).toLocaleString() : ''}
                          {event.by ? ` • ${event.by}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* A2: CO-scope attachment upload */}
      <AttachModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        /* IDI-460: was scope="quote" — a change order's files were stored
           as a QUOTE's attachments under the CO's id, so they were lost. */
        scope="changeOrder"
        ownerId={id}
        ownerLabel={co?.name || `CO ${id?.slice(0, 8)}`}
        companyId={co?.companyId}
        onAttached={() => fetchChangeOrder()}
      />

      {/* IDI-460 (D-06): Add to Job's final check. */}
      <ApplyConfirmDialog
        preview={applyPreview}
        onClose={() => setApplyPreview(null)}
        onConfirm={confirmApply}
      />

      {/* IDI-460 / IDI-547: Record Decision captures a note (no channel). */}
      <RecordDecisionDialog
        open={decisionOpen}
        onClose={() => setDecisionOpen(false)}
        onSubmit={submitRecordDecision}
        submitting={actionLoading}
      />
    </div>
  );
}

/**
 * IDI-460 (D1–D3): Add to Job's confirm. Rows, "Net change", the tax caption
 * (and amount when known) and "New job total" = the job's contract total +
 * the view's totalCents, which is what apply writes (IDI-560). Tax or the
 * job's total unknown: caption only, no new total; it still applies.
 */
export function ApplyConfirmDialog({ preview, onClose, onConfirm }) {
  if (!preview) return null;
  const { view, job } = preview;
  const lines = Array.isArray(view.lines) ? view.lines : [];
  const tax = view.tax || {};
  const jobTotal = getJobContractTotal(job);
  const coTotal = Number.isFinite(Number(view.totalCents)) && view.totalCents !== null ? Number(view.totalCents) : null;
  const newTotal = tax.known && jobTotal && coTotal !== null ? jobTotal.totalCents + coTotal : null;
  const caption = tax.caption ?? '';
  return (
    <Dialog open onClose={onClose} size="lg">
      <DialogHeader onClose={onClose}>Add this change order to the job?</DialogHeader>
      <DialogBody>
        <div className="space-y-4" data-testid="co-apply-confirm">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            The change order&apos;s lines will be merged into the job and the job&apos;s total will be updated.
          </p>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Item</TableHeader>
                <TableHeader>Change</TableHeader>
                <TableHeader align="right">Quantity</TableHeader>
                <TableHeader align="right">Amount</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => <LineRow key={`${l.kind}-${i}`} l={l} />)}
            </TableBody>
          </Table>
          <dl className="ml-auto max-w-xs space-y-1 text-sm">
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-text-muted)' }}>Net change</dt>
              <dd className="tabular-nums" data-testid="co-apply-net">{formatCurrency(view.netChangeCents)}</dd>
            </div>
            {tax.known ? (
              <div className="flex justify-between">
                <dt style={{ color: 'var(--color-text-muted)' }}>{caption || 'Tax'}</dt>
                <dd className="tabular-nums" data-testid="co-apply-tax">{formatCurrency(tax.cents)}</dd>
              </div>
            ) : (
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }} data-testid="co-apply-tax-unknown">
                {caption || 'Tax unavailable'}
              </div>
            )}
            {newTotal !== null && (
              <div className="flex justify-between pt-1 border-t font-semibold" style={{ borderColor: 'var(--color-border)' }}>
                <dt style={{ color: 'var(--color-text-primary)' }}>New job total</dt>
                <dd className="tabular-nums" data-testid="co-apply-new-total">{formatCurrency(newTotal)}</dd>
              </div>
            )}
          </dl>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={onConfirm}>Add to Job</Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

// IDI-460: the manual-acceptance surface for a change order.
//
// Captures who (the signed-in user, server-side), when (server-side) and
// notes — as a dialog, because the quote's page exists to CONVERT a quote to
// a job, which has no meaning for an amendment to a job that already exists.
// IDI-547: no channel picker (Adrian 10-20: change orders follow the quote
// rule). A change order is either e-signed or accepted manually.

/**
 * IDI-460 v2 (1): the decision a user recorded, read back from the CO.
 * record-decision stores decisionChannel / decisionNotes / acknowledgedAt;
 * before this nothing displayed them, so a note was write-only.
 * Renders nothing unless a decision exists.
 * IDI-547: reads "E-signed [by X] on <date> at <time>" or "Accepted manually
 * on <date> at <time>" — never a channel name, never the raw "e_sign".
 */
export function RecordedDecision({ co }) {
  if (!co?.decisionChannel && !isESigned(co)) return null;
  const notes = typeof co.decisionNotes === 'string' ? co.decisionNotes.trim() : '';
  return (
    <div className="pt-3 space-y-1" style={{ borderTop: '1px solid var(--color-border)' }}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">Decision recorded</span>
        <span className="text-xs text-gray-700" data-testid="co-decision-line">{acceptedLine(co)}</span>
      </div>
      {co.decisionRecorder && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Recorded by</span>
          <span className="text-xs text-gray-700">{co.decisionRecorder}</span>
        </div>
      )}
      {notes && (
        <p className="text-xs text-gray-700 whitespace-pre-wrap" data-testid="co-decision-notes-readback">
          {notes}
        </p>
      )}
    </div>
  );
}

export function RecordDecisionDialog({ open, onClose, onSubmit, submitting = false }) {
  const [notes, setNotes] = useState('');

  if (!open) return null;
  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogHeader onClose={onClose}>Record customer acceptance</DialogHeader>
      <DialogBody>
        <div className="space-y-4">
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Records that the customer accepted this change order outside e-signature.
            It will be marked accepted and cannot be undone from here.
          </p>
          <div>
            <label htmlFor="co-decision-notes" className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Notes (optional)
            </label>
            <textarea
              id="co-decision-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Who you spoke to, what they agreed to…"
              className="w-full rounded-md border p-2 text-sm"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-surface-default)',
                color: 'var(--color-text-primary)',
              }}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSubmit({ notes })} loading={submitting}>
              Record Acceptance
            </Button>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}
